import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchEventExposure, getEventById, selectEventState } from "src/redux/reducers/eventSlice";
import { useAppDispatch, useAppSelector } from "src/redux/redux-hooks";
import { EventDetail, MarketEventI, MarketFancyI } from "src/types/eventDetail";
import clientInstance from "src/api/clientInstance";
import apiInstance from "src/api/instance";
import endpoints from "src/api/endpoints";
import { analysisApi, MasterUserPlUserData } from "src/api/analysisApi";
import { isRacing } from "src/utils/functions";
import socketServices from "src/sockets/socketService";
import { DEBUG_SOCKET } from "src/config/constants";
import { useSocketRoomIds } from "src/sockets/socketRoomIdProvider";
import eventSocket from "src/sockets/eventSocket";
import fancySocketServices from "src/sockets/fancySocketServices";
import { formatUnix } from "src/utils/date";
import sortByName from "src/utils/short-by-name";
import useRefreshBetList from "src/sockets/useRefreshBetList";
import MarketTable from "./market-table";
import FancyMarkets from "./fancy-markets";
import useConsole from "src/hooks/useConsole";
import AnalysisRightBar from "./analysis-right-bar";
import SocketInitialization from "src/SocketInitialization";

const artifacts = { is_socket: true };
const activeState = "active";

interface DownlineUser {
  user_id: string;
  user_name: string;
  display_name?: string;
}

interface UserBookRow {
  id: string;
  userName: string;
  profitsByRunner: Record<string, number>;
}

const EventMarketAnalysis = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { eventId, providerId } = useParams();
  const { isLoggedIn, userData } = useAppSelector((state) => state.user);
  const { eventSocketConnected, oddsSocketConnected, fancySocketConnected } = useAppSelector(({ socket }) => socket);

  const [event, setEvent] = useState<EventDetail>();
  const [bookmakerMarkets, setBookmakerMarkets] = useState<MarketEventI[]>([]);
  const [matchOddMarkets, setMatchOddMarkets] = useState<MarketEventI[]>([]);
  const [fancyMarkets, setFancyMarkets] = useState<MarketFancyI[]>([]);
  const [score, setScore] = useState<any | null>(null);
  const [isBetLockOpen, setIsBetLockOpen] = useState(false);
  const [transactionCode, setTransactionCode] = useState("");
  const [downlineUsers, setDownlineUsers] = useState<DownlineUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isUserBookOpen, setIsUserBookOpen] = useState(false);
  const [userBookMarketId, setUserBookMarketId] = useState("");
  const [userBookMarketName, setUserBookMarketName] = useState("");
  const [userBookLoading, setUserBookLoading] = useState(false);
  const [userBookError, setUserBookError] = useState("");
  const [userBookRows, setUserBookRows] = useState<UserBookRow[]>([]);
  const [userBookRunnerColumns, setUserBookRunnerColumns] = useState<string[]>([]);

  const { getSocketFancyIds, resetFancyIds, getSocketEventId, handleEventRoomIds, getSocketOddsIds, resetOddsIds } =
    useSocketRoomIds();

  const handleGetEventById = () => {
    if (eventId) {
      dispatch(getEventById({ eventId, is_fancy: !isLoggedIn }))
        .unwrap()
        .then((event) => {
          if (!event || !event?.provider_event_id || !event?.game_name) {
            //  enqueueSnackbar("No Markets are active", { variant: "error" });
            navigate("/ ");
            return;
          }

          setEvent(event);
          setFancyMarkets(Array.isArray(event?.fancy) ? event.fancy : []);
          setBookmakerMarkets(Array.isArray(event?.bookmaker) ? event.bookmaker : []);
          setMatchOddMarkets(Array.isArray(event?.match_odd) ? event.match_odd : []);
        })
        .catch(() => {
          // enqueueSnackbar("No Markets are active", { variant: "error" });
          navigate("/");
        });
    } else {
      // setSocketEvent(null);
      navigate("/");
    }
  };

  useEffect(() => {
    handleGetEventById();
  }, [eventId, isLoggedIn]);

  const eventLoaded = useMemo(() => {
    return event ? true : false;
  }, [event]);

  useEffect(() => {
    if (isLoggedIn && !artifacts?.is_socket && !eventLoaded && activeState === "active") {
      const returnUpdatedMarket = (prev: MarketEventI[], nMarketMap: any) => {
        return Array.isArray(prev)
          ? [...prev].map((curMarket: MarketEventI) => {
              const newMarket = nMarketMap[curMarket.provider_market_id];

              if (newMarket) {
                const newRunnerMap: any = {};
                if (Array.isArray(newMarket?.r)) {
                  newMarket?.r.forEach((runner: any) => {
                    newRunnerMap[runner.id] = runner;
                  });
                }

                const updatedRunners = Array.isArray(curMarket?.runners)
                  ? [...curMarket.runners].map((curRunner) => {
                      const newRunner = newRunnerMap[curRunner.runner_id];

                      if (newRunner) {
                        // console.log('API RUNNER UPDATED');
                        return {
                          ...curRunner,
                          ...newRunner,
                          provider_status: newRunner.ps,
                          back: newRunner.b,
                          lay: newRunner.l,
                        };
                      }
                      return { ...curRunner };
                    })
                  : [];
                // console.log('API MARKET UPDATED');
                return { ...curMarket, ...newMarket, provider_status: newMarket.ps, runners: updatedRunners };
              } else {
                return { ...curMarket };
              }
            })
          : [];
      };

      const getOddsApi = () => {
        clientInstance
          .get(`/event/all_market_list/${eventId}`)
          .then(({ data }) => {
            if (Array.isArray(data?.data?.match_odd)) {
              const newOddMap: any = {};
              data.data.match_odd.forEach((m: any) => {
                newOddMap[m.pm] = m;
              });

              setMatchOddMarkets((prev) => returnUpdatedMarket(prev, newOddMap));
            }

            if (Array.isArray(data?.data?.bookmaker)) {
              const newBookmakerMap: any = {};
              data.data.bookmaker.forEach((m: any) => {
                newBookmakerMap[m.pm] = m;
              });

              setBookmakerMarkets((prev) => returnUpdatedMarket(prev, newBookmakerMap));
            }
          })
          .catch((err) => {
            console.log(err);
          });
      };

      const interval = setInterval(() => {
        getOddsApi();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [eventId, isLoggedIn, artifacts?.is_socket, eventLoaded, activeState]);

  const gameType = useMemo(() => {
    return providerId && isRacing(providerId) ? "race" : "sport";
  }, [providerId]);

  const { allEvents } = useAppSelector(selectEventState);
  useConsole("🚀 ~ fetchEventExposure ~ allEvents:", allEvents);

  const sEventExposure = useMemo(() => {
    if (Array.isArray(allEvents)) {
      return allEvents.find((et: any) => et.event_id === eventId);
    }

    return null;
  }, [allEvents, eventId]);

  const getEventExposure = () => {
    if (userData?.user_id) {
      dispatch(fetchEventExposure(userData.user_id));
    }
    // if (eventId && showLiveBet) {
    //   dispatch(getLiveBets({ event_id: eventId, market_id: marketId, limit: 1000, offset: 1, updateRdx: true }));
    // }
  };

  useEffect(() => {
    if (userData?.user_id) {
      getEventExposure();
    }
  }, [dispatch, userData?.user_id]);

  useEffect(() => {
    if (!isBetLockOpen || !userData?.user_id) return;

    let mounted = true;
    apiInstance
      .get(endpoints.member.getAccountList(userData.user_id, 1000, true))
      .then(({ data }) => {
        if (!mounted) return;
        const list = Array.isArray(data?.data?.downLine)
          ? data.data.downLine
          : Array.isArray(data?.data)
            ? data.data
            : [];
        setDownlineUsers(list);
      })
      .catch(() => {
        if (!mounted) return;
        setDownlineUsers([]);
      });

    return () => {
      mounted = false;
    };
  }, [isBetLockOpen, userData?.user_id]);

  const toggleSelectAllUsers = () => {
    if (selectedUsers.length === downlineUsers.length) {
      setSelectedUsers([]);
      return;
    }
    setSelectedUsers(downlineUsers.map((item) => item.user_id));
  };

  const toggleSelectUser = (userId: string) => {
    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((item) => item !== userId) : [...prev, userId]));
  };

  const normalizeRunnerName = (name?: string) => String(name || "-").trim();

  const buildUserBookData = (list: MasterUserPlUserData[]) => {
    const runnerSet = new Set<string>();
    const rows: UserBookRow[] = [];

    const walk = (nodes: MasterUserPlUserData[]) => {
      nodes.forEach((node, index) => {
        const profitsByRunner: Record<string, number> = {};

        if (Array.isArray(node.runners)) {
          node.runners.forEach((runner) => {
            const runnerName = normalizeRunnerName(runner.runner_name);
            runnerSet.add(runnerName);
            profitsByRunner[runnerName] = Number(runner.profit || 0);
          });
        }

        rows.push({
          id: node.id || `${node.user_name || "user"}-${index}`,
          userName: node.user_name || "-",
          profitsByRunner,
        });

        if (Array.isArray(node.children) && node.children.length > 0) {
          walk(node.children);
        }
      });
    };

    walk(list);
    return {
      rows,
      runnerColumns: Array.from(runnerSet),
    };
  };

  const formatBookValue = (value: number) => {
    if (!Number.isFinite(value)) return "0";
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2);
  };

  useEffect(() => {
    if (!isUserBookOpen || !userBookMarketId) return;
    let mounted = true;
    setUserBookLoading(true);
    setUserBookError("");

    analysisApi
      .fetchMasterUserPl({ marketId: userBookMarketId, role: "User" })
      .then((response) => {
        if (!mounted) return;
        if (response.response_error) {
          setUserBookError(response.status_message || "Failed to load user book.");
          setUserBookRows([]);
          setUserBookRunnerColumns([]);
          return;
        }
        const result = buildUserBookData(Array.isArray(response.data) ? response.data : []);
        setUserBookRows(result.rows);
        setUserBookRunnerColumns(result.runnerColumns);
      })
      .catch(() => {
        if (!mounted) return;
        setUserBookError("Failed to load user book.");
        setUserBookRows([]);
        setUserBookRunnerColumns([]);
      })
      .finally(() => {
        if (!mounted) return;
        setUserBookLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isUserBookOpen, userBookMarketId]);

  // -------- SOCKET START --------

  // API call on socket EVENT
  useRefreshBetList(getEventExposure, isLoggedIn);

  useEffect(() => {
    if (oddsSocketConnected && eventId && gameType && activeState === "active")
      socketServices.refreshBookmakerList(() => {
        if (gameType === "sport") handleGetEventById();
        if (gameType === "race") handleGetEventById();
      });
  }, [oddsSocketConnected, eventId, gameType, activeState]);

  const handleFancySocket = (data: { data: any; response_error: any }) => {
    if (DEBUG_SOCKET) console.log("FANCY", data?.data);
    if (data && !data.response_error && Array.isArray(data.data.fancy)) {
      setFancyMarkets(data.data.fancy);
    } else {
      setFancyMarkets([]);
    }
  };

  const leaveEventRoom = () => {
    if (eventSocketConnected) {
      const data = getSocketEventId();
      if (data) {
        if (DEBUG_SOCKET) console.log(`🚀 ~ LEAVE_EVENT:`, data);
        eventSocket.leaveEvent(data);
      }
    }
  };

  const leaveOddsRoom = () => {
    const socketOddsIds: any[] = getSocketOddsIds();
    if (DEBUG_SOCKET) console.log(`🚀 ~ socketOddsIds !!:`, socketOddsIds);
    if (socketOddsIds.length > 0 && oddsSocketConnected) {
      socketOddsIds.forEach((data: any) => {
        if (DEBUG_SOCKET) console.log(`🚀 ~ LEAVE_ROOM:`, data);
        socketServices.leaveRoom(data);
      });
      resetOddsIds();
    }
  };

  const leaveFancyRoom = () => {
    const socketFancyIds: any[] = getSocketFancyIds();
    if (DEBUG_SOCKET) console.log(`🚀 ~ socketFancyIds !!:`, socketFancyIds);
    if (socketFancyIds.length > 0 && fancySocketConnected) {
      socketFancyIds.forEach((data: any) => {
        if (DEBUG_SOCKET) console.log(`🚀 ~ LEAVE_FANCY_ROOM:`, data);
        fancySocketServices.leaveRoom(data);
      });
      resetFancyIds();
    }
  };

  const leaveAllRoom = () => {
    leaveEventRoom();
    leaveOddsRoom();
    leaveFancyRoom();
  };

  useEffect(() => {
    if (!isLoggedIn) leaveAllRoom();
  }, [isLoggedIn]);

  useLayoutEffect(() => {
    // Leave event rooms when user leave page
    return () => {
      leaveEventRoom();
    };
  }, [eventId, eventSocketConnected]);

  useLayoutEffect(() => {
    // Leave oddd rooms when user leave page
    return () => {
      leaveOddsRoom();
    };
  }, [eventId, oddsSocketConnected]);

  useLayoutEffect(() => {
    // Leave fancy rooms when user leave page
    return () => {
      leaveFancyRoom();
    };
  }, [eventId, fancySocketConnected]);

  useEffect(() => {
    // joining event
    if (event && eventSocketConnected && isLoggedIn && activeState === "active") {
      if (DEBUG_SOCKET) console.log(`🚀 ~ EVENT JOIN_EVENT:`, { eventId: event.provider_event_id });
      handleEventRoomIds({ eventId: event.provider_event_id });

      eventSocket.joinEvent({ eventId: event.provider_event_id });
      // eventSocket.getEventDetails(setSocketEvent); // TODO: get event details from socket

      if (event.is_score_available) {
        eventSocket.getLiveScore((data) => {
          setScore(data);
        });
      }
    }
  }, [event, eventSocketConnected, isLoggedIn, activeState]);

  const returnUpdateSocketOdd = (prev: MarketEventI[], newMarket: any) => {
    return [...prev].map((curMarket) => {
      if (curMarket.provider_market_id === newMarket?.pm) {
        const newRunnerMap: any = {};
        if (Array.isArray(newMarket?.r)) {
          newMarket?.r.forEach((runner: any) => {
            newRunnerMap[runner.id] = runner;
          });
        }

        const updatedRunners = Array.isArray(curMarket?.runners)
          ? [...curMarket.runners].map((curRunner) => {
              const newRunner = newRunnerMap[curRunner.runner_id];

              if (newRunner) {
                // console.log('SOCKET RUNNER UPDATED');
                return {
                  ...curRunner,
                  ...newRunner,
                  provider_status: newRunner.ps,
                  back: newRunner.b,
                  lay: newRunner.l,
                };
              }
              return { ...curRunner };
            })
          : [];

        // console.log('SOCKET MARKET UPDATED');
        return {
          ...curMarket,
          ...newMarket,
          provider_status: newMarket.ps,
          runners: updatedRunners,
        };
      }

      return { ...curMarket };
    });
  };

  useEffect(() => {
    const getMatchOdds = () => {
      if (event?.match_odd && Array.isArray(event?.match_odd) && event?.match_odd.length > 0) {
        event.match_odd.forEach((m) => {
          const roomName = `${m.provider_event_id}/${m.provider_market_id}`;

          const socketData = {
            roomNames: roomName,
            marketType: m?.market_type,
          };
          if (DEBUG_SOCKET) console.log(`🚀 ~ EVENT JOIN ODDS:`, socketData);

          socketServices.joinRoom(socketData);
          socketServices.listenEvent(roomName, (data) => {
            console.log("MATCH_ODD", data?.data);
            // if (DEBUG_SOCKET) {
            //   console.group(data.data.market_name ?? 'ODDS');
            //   console.table(
            //     Array.isArray(data.data.runners)
            //       ? data.data.runners.map((run: MarketRunnerI) => {
            //           return {
            //             name: run.runner_name,
            //             back: Array.isArray(run.back)
            //               ? run.back
            //                   .map((b) => b.price.toString())
            //                   .reverse()
            //                   .join(' | ')
            //               : '',
            //             lay: Array.isArray(run.lay) ? run.lay.map((b) => b.price).join(' | ') : '',
            //           };
            //         })
            //       : []
            //   );
            //   console.groupEnd();
            // }

            setMatchOddMarkets((prev) => returnUpdateSocketOdd(prev, data.data));
          });
        });
      }
    };
    if (oddsSocketConnected && artifacts?.is_socket && activeState === "active") {
      getMatchOdds();
    }
  }, [oddsSocketConnected, event?.match_odd, artifacts?.is_socket, activeState]);

  useEffect(() => {
    const getBookmaker = () => {
      if (event?.bookmaker && Array.isArray(event?.bookmaker) && event?.bookmaker.length > 0) {
        event.bookmaker.forEach((m) => {
          const roomName = `${m.provider_event_id}/${m.provider_market_id}`;

          const socketData = {
            roomNames: roomName,
            marketType: m?.market_type,
          };
          if (DEBUG_SOCKET) console.log(`🚀 ~ EVENT JOIN BOOKMAKER:`, socketData);

          socketServices.joinRoom(socketData);
          socketServices.listenEvent(roomName, (data) => {
            console.log("BOOKMAKER", data?.data);
            // if (DEBUG_SOCKET) {
            //   console.group(data.data.market_name ?? 'ODDS');
            //   console.table(
            //     Array.isArray(data.data.runners)
            //       ? data.data.runners.map((run: MarketRunnerI) => {
            //           return {
            //             name: run.runner_name,
            //             back: Array.isArray(run.back)
            //               ? run.back
            //                   .map((b) => b.price.toString())
            //                   .reverse()
            //                   .join(' | ')
            //               : '',
            //             lay: Array.isArray(run.lay)
            //               ? run.lay.map((b) => b.price.toString()).join(' | ')
            //               : '',
            //           };
            //         })
            //       : []
            //   );
            //   console.groupEnd();
            // }

            setBookmakerMarkets((prev) => returnUpdateSocketOdd(prev, data.data));
          });
        });
      }
    };
    if (oddsSocketConnected && artifacts?.is_socket && activeState === "active") {
      getBookmaker();
    }
  }, [oddsSocketConnected, event?.bookmaker, artifacts?.is_socket, activeState]);

  useEffect(() => {
    const getFancyMarkets = () => {
      // joining room for fancy markets
      if (event && event.provider_event_id) {
        const data = {
          roomNames: event.provider_event_id,
          marketType: "fancy",
        };
        if (DEBUG_SOCKET) console.log(`🚀 ~ EVENT JOINING FANCY:`, data);
        fancySocketServices.joinRoom(data);
        fancySocketServices.listenEvent(event.provider_event_id, handleFancySocket);
      }
    };
    if (isLoggedIn && fancySocketConnected && event?.game_name === "cricket" && activeState === "active") {
      getFancyMarkets();
    }
  }, [event, isLoggedIn, fancySocketConnected, activeState]);

  // -------- SOCKET END --------

  const { sortedMatchOdds, sortedBookmakers, tiedMatchMarkets } = useMemo(() => {
    let sortedMatchOdds: MarketEventI[] = [];
    let sortedBookmakers: MarketEventI[] = [];
    let tiedMatchMarkets: MarketEventI[] = [];

    if (Array.isArray(matchOddMarkets)) {
      matchOddMarkets.forEach((m) => {
        if (m.market_name && m.market_name.toLowerCase() === "tied match") {
          tiedMatchMarkets.push(m);
        } else {
          sortedMatchOdds.push(m);
        }
      });
    }

    sortedMatchOdds = [...sortedMatchOdds].filter((m) => m.is_active && m.market_type);
    sortedBookmakers = [...bookmakerMarkets].filter((m) => m.is_active && m.market_type);

    const oddMarketOrderMap: { [key: string]: number } = {
      "match odds": 0,
    };

    sortedMatchOdds = sortByName({ list: sortedMatchOdds, orderMap: oddMarketOrderMap, shortKey: "market_name" });

    const bookmakerOrderMap: { [key: string]: number } = {
      bookmaker: 0,
      "mini bookmaker": 1,
    };

    sortedBookmakers = sortByName({ list: sortedBookmakers, orderMap: bookmakerOrderMap, shortKey: "market_name" });

    return {
      sortedMatchOdds,
      sortedBookmakers,
      tiedMatchMarkets,
    };
  }, [bookmakerMarkets, matchOddMarkets]);

  const FANCY_CATEGORIES = ["RUNS", "OVERS", "BATSMAN", "SINGLE_OVER"];
  const fancyFilter = useMemo(() => {
    if (Array.isArray(fancyMarkets)) {
      return fancyMarkets.filter((market) => FANCY_CATEGORIES.includes(market.category) && market.yes_value !== 1);
    }

    return [];
  }, [fancyMarkets]);

  return (
    <div className="detail-page-container">
      <SocketInitialization />
      <div className="center-main-container">
        {event && (
          <>
            <div className="center-content">
              <div className="game-header">
                <span className="game-header-name">{`${event?.league_name} > ${event?.event_name}`}</span>
                <span>
                  <span>{formatUnix({ unix: event.event_time, format: "DD/MM/YYYY HH:mm:ss" })}</span>
                </span>
              </div>

              <div className="market-container mt-0">
                {Array.isArray(sortedMatchOdds) &&
                  sortedMatchOdds.map((mItem) => {
                    return (
                      <MarketTable
                        key={mItem.market_id}
                        marketExposureList={
                          mItem.market_type === "match_odd"
                            ? sEventExposure?.matchOddMarkets
                            : sEventExposure?.bookMakerMarkets
                        }
                        sMarket={mItem}
                        event={event}
                        onBetLockClick={() => setIsBetLockOpen(true)}
                        onUserBookClick={(marketId, marketName) => {
                          setUserBookMarketId(marketId);
                          setUserBookMarketName(marketName);
                          setIsUserBookOpen(true);
                        }}
                      />
                    );
                  })}

                {Array.isArray(sortedBookmakers) &&
                  sortedBookmakers.map((mItem) => {
                    return (
                      <MarketTable
                        key={mItem.market_id}
                        marketExposureList={
                          mItem.market_type === "match_odd"
                            ? sEventExposure?.matchOddMarkets
                            : sEventExposure?.bookMakerMarkets
                        }
                        sMarket={mItem}
                        event={event}
                        onBetLockClick={() => setIsBetLockOpen(true)}
                        onUserBookClick={(marketId, marketName) => {
                          setUserBookMarketId(marketId);
                          setUserBookMarketName(marketName);
                          setIsUserBookOpen(true);
                        }}
                      />
                    );
                  })}

                {Array.isArray(tiedMatchMarkets) &&
                  tiedMatchMarkets.map((mItem) => {
                    return (
                      <MarketTable
                        key={mItem.market_id}
                        marketExposureList={
                          mItem.market_type === "match_odd"
                            ? sEventExposure?.matchOddMarkets
                            : sEventExposure?.bookMakerMarkets
                        }
                        sMarket={mItem}
                        event={event}
                        onBetLockClick={() => setIsBetLockOpen(true)}
                        onUserBookClick={(marketId, marketName) => {
                          setUserBookMarketId(marketId);
                          setUserBookMarketName(marketName);
                          setIsUserBookOpen(true);
                        }}
                      />
                    );
                  })}

                {fancyFilter.length > 0 && (
                  <FancyMarkets list={fancyFilter} eventExposure={sEventExposure} onBetLockClick={() => setIsBetLockOpen(true)} />
                )}
              </div>
            </div>
            <AnalysisRightBar event={event} score={score} />
          </>
        )}
      </div>
      {isBetLockOpen && (
        <div style={{ position: "absolute", zIndex: 1040 }}>
          <div className="modal fade show" role="dialog" aria-modal="true" style={{ display: "block" }}>
            <div className="modal-dialog modal-md">
              <span tabIndex={0}></span>
              <div className="modal-content" tabIndex={-1}>
                <header className="modal-header">
                  <h5 className="modal-title">Bet Lock</h5>
                  <button
                    type="button"
                    aria-label="Close"
                    className="close"
                    onClick={() => {
                      setIsBetLockOpen(false);
                      setTransactionCode("");
                      setSelectedUsers([]);
                    }}
                  >
                    ×
                  </button>
                </header>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-6"></div>
                    <div className="col-6">
                      <div className="form-group">
                        <input
                          type="password"
                          name="ulockmpass"
                          value={transactionCode}
                          onChange={(e) => setTransactionCode(e.target.value)}
                          placeholder="Transaction Code"
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>
                            <div className="custom-control custom-checkbox">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                value="1"
                                id="bet-lock-all-users"
                                checked={downlineUsers.length > 0 && selectedUsers.length === downlineUsers.length}
                                onChange={toggleSelectAllUsers}
                                aria-label="Select all accounts"
                              />
                              <label className="custom-control-label" htmlFor="bet-lock-all-users">
                                <span className="sr-only">Select all accounts</span>
                              </label>
                            </div>
                          </th>
                          <th>All Account</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!downlineUsers.length ? (
                          <tr>
                            <td colSpan={2} className="text-center">
                              No accounts found
                            </td>
                          </tr>
                        ) : (
                          downlineUsers.map((user, index) => (
                            <tr key={user.user_id}>
                              <td>
                                <div className="custom-control custom-checkbox">
                                  <input
                                    type="checkbox"
                                    name="locku"
                                    className="custom-control-input"
                                    value={user.user_id}
                                    id={`bet-lock-user-${index}`}
                                    checked={selectedUsers.includes(user.user_id)}
                                    onChange={() => toggleSelectUser(user.user_id)}
                                    aria-label={`Select account ${user.display_name || user.user_name}`}
                                  />
                                  <label className="custom-control-label" htmlFor={`bet-lock-user-${index}`}>
                                    <span className="sr-only">Select account {user.display_name || user.user_name}</span>
                                  </label>
                                </div>
                              </td>
                              <td>{user.display_name || user.user_name}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <span tabIndex={0}></span>
            </div>
          </div>
          <div className="modal-backdrop"></div>
        </div>
      )}
      {isUserBookOpen && (
        <div style={{ position: "absolute", zIndex: 1040 }}>
          <div className="modal fade show" role="dialog" aria-modal="true" style={{ display: "block" }}>
            <div className="modal-dialog modal-big">
              <span tabIndex={0}></span>
              <div className="modal-content" tabIndex={-1}>
                <header className="modal-header">
                  <h5 className="modal-title">User Book - {userBookMarketName || "-"}</h5>
                  <button
                    type="button"
                    aria-label="Close"
                    className="close"
                    onClick={() => {
                      setIsUserBookOpen(false);
                      setUserBookMarketId("");
                      setUserBookMarketName("");
                      setUserBookRows([]);
                      setUserBookRunnerColumns([]);
                      setUserBookError("");
                    }}
                  >
                    ×
                  </button>
                </header>
                <div className="modal-body">
                  {userBookLoading ? <div className="alert alert-info">Loading user book...</div> : null}
                  {!userBookLoading && userBookError ? <div className="alert alert-warning">{userBookError}</div> : null}
                  {!userBookLoading && !userBookError ? (
                    <div className="user-book">
                      <ul className="mtree transit bubba mtree-header">
                        <li className="mtree-node">
                          <a href="#" onClick={(e) => e.preventDefault()}>
                            <table className="table m-b-0 table-striped" style={{ borderBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th></th>
                                  <th className="text-right">UserName</th>
                                  {userBookRunnerColumns.map((runner) => (
                                    <th className="text-right" key={`header-${runner}`}>
                                      {runner}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                            </table>
                          </a>
                        </li>
                      </ul>
                      <ul className="mtree transit bubba user-table">
                        {!userBookRows.length ? (
                          <li className="mtree-node item">
                            <table className="table m-b-0">
                              <tbody>
                                <tr>
                                  <td className="text-center">No records found</td>
                                </tr>
                              </tbody>
                            </table>
                          </li>
                        ) : (
                          userBookRows.map((row) => (
                            <li className="mtree-node mtree-closed item" key={row.id}>
                              <a href="#" className="" onClick={(e) => e.preventDefault()}>
                                <table className="table m-b-0">
                                  <tbody>
                                    <tr>
                                      <td></td>
                                      <td className="text-right">{row.userName}</td>
                                      {userBookRunnerColumns.map((runner) => {
                                        const val = row.profitsByRunner[runner] ?? 0;
                                        return (
                                          <td
                                            className={`text-right ${val < 0 ? "negative" : "positive"}`}
                                            key={`${row.id}-${runner}`}
                                          >
                                            {formatBookValue(val)}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  </tbody>
                                </table>
                              </a>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
              <span tabIndex={0}></span>
            </div>
          </div>
          <div className="modal-backdrop"></div>
        </div>
      )}
    </div>
  );
};

export default EventMarketAnalysis;
